import { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Drawer, Table } from 'antd';
import heIL from 'antd/locale/he_IL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faPrint,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Money } from '../../components/Money';
import { Loader } from '../../components/Loader';
import { useDepositDetail } from '../../hooks/useDeposits';
import { isoToDisplay } from '../../utils/dates';
import { getToken } from '../../api/apiFetch';
import type { DepositReceipt } from '@deposits/shared';

interface Props {
  open: boolean;
  depositId: number | null;
  onClose: () => void;
}

/**
 * Two-pane PDF + details view, matching the Arbox Expenses ExpenseFormPage
 * layout. Right pane (RTL start) = deposit details + receipts table.
 * Left pane (RTL end) = the rendered PDF in an iframe pointed at a same-origin
 * blob URL (so iframe.contentWindow.print() works).
 *
 * Used for both Tab B "פירוט" and "הדפסה" actions, AND from the create-success
 * flow (NewDepositDrawer closes, this opens with the new deposit).
 */
export function DepositPdfDrawer({ open, depositId, onClose }: Props) {
  const { data: detail, isLoading } = useDepositDetail(open ? depositId : null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch the PDF blob through the API (same-origin, Bearer-authed). Cleanup
  // the object URL on close / change to avoid memory leaks.
  useEffect(() => {
    if (!open || depositId == null) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setPdfError(null);
      setPdfLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setPdfLoading(true);
    setPdfError(null);
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`/api/deposits/${depositId}/pdf-blob`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setPdfError((err as Error).message || 'שגיאה');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, depositId]);

  const onDownload = () => {
    if (!blobUrl || !detail) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `deposit-${detail.deposit.deposit_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onPrint = () => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    try {
      w.focus();
      w.print();
    } catch (err) {
      window.alert('שגיאה בפתיחת הדפסה: ' + (err as Error).message);
    }
  };

  const title = detail
    ? `הפקדה מספר ${detail.deposit.deposit_number}`
    : 'פרטי הפקדה';

  return (
    <Drawer
      title={null}
      placement="left"
      open={open}
      onClose={onClose}
      width="min(95vw, 1280px)"
      closable={false}
      styles={{ body: { padding: 0 } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-white">
          <div className="text-heading-3-bold text-main-text">{title}</div>
          <div className="flex items-center gap-2">
            <HeaderButton
              icon={faDownload}
              label="הורדה"
              onClick={onDownload}
              disabled={!blobUrl}
            />
            <HeaderButton
              icon={faPrint}
              label="הדפסה"
              onClick={onPrint}
              disabled={!blobUrl}
              primary
            />
            <button
              onClick={onClose}
              aria-label="סגור"
              className="p-2 rounded hover:bg-grey-bg text-secondary-text hover:text-main-text transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>

        {/* Two-pane body */}
        <div
          className="flex flex-col sm:flex-row"
          style={{ height: 'calc(100vh - 56px)' }}
        >
          {/* Right pane (RTL start) — details */}
          <div className="w-full sm:w-1/2 overflow-auto p-5 border-b sm:border-b-0 sm:border-l border-border bg-white">
            {isLoading || !detail ? (
              <div className="flex items-center justify-center py-12">
                <Loader />
              </div>
            ) : (
              <DetailPanel detail={detail} />
            )}
          </div>

          {/* Left pane (RTL end) — PDF */}
          <div className="w-full sm:w-1/2 bg-grey-bg relative">
            {pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader />
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-error-red text-small px-6 text-center">
                שגיאה בטעינת ה-PDF: {pdfError}
              </div>
            )}
            {blobUrl && (
              <iframe
                ref={iframeRef}
                // toolbar=0 hides Chrome's built-in PDF chrome (page/zoom/
                // download/print/etc). The drawer header has its own הורדה +
                // הדפסה buttons that drive the same iframe, so the native
                // toolbar would be redundant. Note: this PDF-viewer URL
                // fragment is Chrome/Edge-only; Firefox/Safari ignore it but
                // those aren't realistic targets inside the Arbox iframe.
                src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
                title="PDF preview"
                className="w-full h-full border-0 block"
              />
            )}
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function HeaderButton({
  icon,
  label,
  onClick,
  disabled,
  primary,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-1.5 rounded text-small font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        primary
          ? 'bg-primary text-white hover:bg-primary-hover'
          : 'bg-white border border-border text-main-text hover:border-primary'
      }`}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
      <span>{label}</span>
    </button>
  );
}

function DetailPanel({ detail }: { detail: NonNullable<ReturnType<typeof useDepositDetail>['data']> }) {
  const { deposit, receipts } = detail;

  const columns = [
    {
      title: 'תאריך הפקה',
      dataIndex: 'issue_date',
      width: 110,
      render: (v: string) => isoToDisplay(v),
    },
    {
      title: 'מספר קבלה',
      dataIndex: 'receipt_number',
      width: 100,
      render: (v: string) => <span dir="ltr">{v}</span>,
    },
    {
      title: 'שם לקוח',
      dataIndex: 'customer_name',
      ellipsis: true,
    },
    {
      title: 'סכום',
      dataIndex: 'amount',
      width: 100,
      render: (v: number) => <Money amount={v} />,
    },
    {
      title: 'סוג',
      dataIndex: 'payment_type',
      width: 70,
      render: (v: 'cash' | 'check') => (v === 'cash' ? 'מזומן' : "צ'ק"),
    },
    {
      title: 'תאריך פירעון',
      dataIndex: 'due_date',
      width: 110,
      render: (v: string | null) => isoToDisplay(v),
    },
    {
      title: "מספר צ'ק",
      dataIndex: 'check_number',
      width: 100,
      render: (v: string | null) => (v ? <span dir="ltr">{v}</span> : '—'),
    },
    {
      title: 'בנק',
      dataIndex: 'bank_code',
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-y-2 gap-x-3 mb-5 text-base">
        <Field label="תאריך הפקדה">{isoToDisplay(deposit.deposit_date)}</Field>
        <Field label="סטטוס">
          {deposit.cancelled_at ? (
            <span className="text-error-red font-semibold">מבוטל</span>
          ) : (
            <span className="text-success-green">הופקד</span>
          )}
        </Field>
        <Field label='סה"כ מזומן'>
          <Money amount={deposit.cash_total} />
        </Field>
        <Field label='סה"כ צ&apos;קים'>
          <Money amount={deposit.checks_total} />
        </Field>
        <Field label='סה"כ בהפקדה' bold>
          <Money amount={deposit.grand_total} />
        </Field>
        <Field label="יוצר ההפקדה">{deposit.created_by_name || '—'}</Field>
        <Field label="מפקיד">{deposit.depositor_name || '—'}</Field>
        {deposit.notes && (
          <Field label="הערות" colSpan>
            {deposit.notes}
          </Field>
        )}
      </div>

      <div className="font-semibold mb-2">תקבולים בהפקדה זו</div>
      <Table<DepositReceipt>
        rowKey="id"
        columns={columns}
        dataSource={receipts}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
}

function Field({
  label,
  children,
  bold,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  bold?: boolean;
  colSpan?: boolean;
}) {
  return (
    <>
      <div className={`text-secondary-text ${colSpan ? 'col-span-2 mt-2' : ''}`}>
        {label}
      </div>
      <div
        className={`${colSpan ? 'col-span-2' : 'text-end'} ${bold ? 'font-semibold' : ''}`}
      >
        {children}
      </div>
    </>
  );
}
